import {FC, useMemo, useState} from 'react'
import {useStore} from '../../store'
import {useTemplateMode} from '../template/useTemplateMode'
import {GroupModel} from '../../core/_models'
import {FdsSelect, fitWidth} from './FdsSelect'

// Last few stores this browser switched to, newest first. A convenience only:
// storage can be blocked or cleared, and the picker must work without it.
const RECENT_KEY = 'fds-scheduler-recent-locations'
const RECENT_LIMIT = 3

const readRecent = (): number[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'number') : []
  } catch (e) {
    return []
  }
}

const writeRecent = (ids: number[]) => {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(ids))
  } catch (e) {
    // storage unavailable — the picker just has no recents
  }
}

type LocationOption = {
  id: number
  name: string
  group: 'Recent' | 'All stores'
}

/**
 * The store switcher.
 *
 * A native select of every store is fine for five; managers with dozens had to
 * scroll a list in database order to find theirs. This is a type-to-filter
 * combobox, alphabetical, with the stores this browser used last pinned on top
 * under "Recent" (they appear again under "All stores", so the alphabetical list
 * stays complete). With a single store there is nothing to choose, so it is a
 * plain label.
 *
 * Its own component subscribing to the store, not inline toolbar JSX: the
 * toolbar is only rebuilt on [laborSchedule], so inline JSX would capture stale
 * store flags — and the template-mode leave-guard needs current ones (a
 * location change discards the in-progress draft).
 */
const LocationSelect: FC<{locations: GroupModel[] | null}> = ({locations}) => {
  const {selectedLocationId, handleState} = useStore()
  const {templateMode, requestExit} = useTemplateMode()
  const [recent, setRecent] = useState<number[]>(readRecent)

  const options: LocationOption[] = useMemo(() => {
    const all = (locations || [])
      .map((l) => ({id: l.location_id, name: l.name}))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, {numeric: true}))
    // Fewer than eight stores fit on screen anyway; a Recent group would only
    // repeat what is already visible.
    if (all.length < 8) return all.map((l) => ({...l, group: 'All stores' as const}))
    const byId = new Map(all.map((l) => [l.id, l]))
    const recents = recent
      .filter((id) => id !== selectedLocationId && byId.has(id))
      .map((id) => ({...byId.get(id)!, group: 'Recent' as const}))
    return [...recents, ...all.map((l) => ({...l, group: 'All stores' as const}))]
  }, [locations, recent, selectedLocationId])

  const selected = options.find((o) => o.id === selectedLocationId && o.group === 'All stores')

  const changeLocation = (id: number) => {
    // Remember where we came FROM as well as where we went, so flipping
    // between two stores keeps both one click away.
    const next = [id, selectedLocationId, ...recent]
      .filter((v): v is number => typeof v === 'number')
      .filter((v, i, arr) => arr.indexOf(v) === i)
      .slice(0, RECENT_LIMIT + 1)
    setRecent(next)
    writeRecent(next)
    handleState(id, 'selectedLocationId')
  }

  if (locations && locations.length === 1) {
    return (
      <span className='fs-7 fw-bold text-gray-800 me-2 text-truncate mw-200px'>
        {locations[0].name}
      </span>
    )
  }

  return (
    <div className='me-2'>
      <FdsSelect
        ariaLabel='Store'
        options={options.map((o) => ({value: o.id, label: o.name, group: o.group}))}
        value={selectedLocationId}
        // Sized to the store shown, not the longest name in the company: a
        // fixed width wide enough for every store left most of them floating
        // in an oversized box. The open list is wider than the field.
        width={fitWidth([selected?.name || 'Search stores…'], 10, 26)}
        menuMinWidth={260}
        placeholder='Search stores…'
        noOptionsText='No matching store'
        onChange={(id) => {
          if (templateMode) {
            requestExit(() => changeLocation(id))
          } else {
            changeLocation(id)
          }
        }}
      />
    </div>
  )
}

export {LocationSelect}
